// Content for /for/business — multi-site commercial and hospitality.
//
// TRACE: see the note at the top of schools.js.
//
// The `business` org flavour now exists (openspec change business-org-flavour), so this page may
// use the vocabulary the product actually shows these customers: Sites, and Closures rather than
// a landlord's "Properties" and "voids". Keep the two in step — if you add a term here, check it
// against the `business` column in `lib/terminology.js`. Still avoid venue-specific language
// ("covers", "front of house") the product does not model at all.

export default {
    key: 'business',
    eyebrow: 'For pubs, restaurants, retail and multi-site operators',
    title: 'Find out what your sites are burning after closing time',
    subtitle:
        'Most multi-site energy waste happens when nobody is there. Half-hourly data across every site shows you exactly how much, and where.',
    heroChips: [
        'Out-of-hours analysis',
        'Baseload alerts',
        'Site-by-site ranking',
        'Logins for site managers'
    ],

    features: [
        {
            title: 'Out-of-hours consumption, quantified',
            detail: 'Split every site’s usage into open and closed hours and see what the closed half is costing you. It is usually the cheapest saving on the table.',
            capability: 'out-of-hours-insights'
        },
        {
            title: 'Opening hours the data respects',
            detail: 'Set each site’s trading hours so "out of hours" means your actual pattern, not a generic nine-to-five: split shifts, late trading past midnight, summer and winter hours, and bank holidays for England, Scotland, Wales or Northern Ireland.',
            capability:
                'occupancy-schedule / sector-neutral-calendar / estate-per-location-cost-occupancy'
        },
        {
            title: 'An alert when a closed site is using energy',
            detail: 'Out-of-hours and closed-day alerts follow each site’s own hours, so a kitchen left on after a late close or a shut site that is still drawing power is flagged the next morning.',
            capability: 'alerts-rules / out-of-hours-insights'
        },
        {
            title: 'See what the kitchen and the cellar use',
            detail: 'Tag sub-meters as kitchen, refrigeration, cellar cooling, plant and HVAC or EV charging, then break usage down by category, explore it, and set alerts on just one of them.',
            capability: 'meter-categories / power-explore-export / alerts-rules'
        },
        {
            title: 'Catch equipment left running',
            detail: 'Baseload-creep alerts fire when a site’s overnight floor rises: the signature of a fridge, fan or heater that stopped switching off.',
            capability: 'baseload-creep-alert'
        },
        {
            title: 'Rank your sites against each other',
            detail: 'A league table of every site for the period, by consumption, cost or out-of-hours share, with each one’s movement against the previous period, so the site that is slipping is obvious. Record each site’s floor area and headcount and the same table also ranks by kWh per m² and per head, so a small bar and a large venue can be compared fairly.',
            capability:
                'location-profile-fields / estate-property-portfolio / portfolio-rankings-ui'
        },
        {
            title: 'Know the cost, check every line of the bill',
            detail: 'Tariff-based cost per site including standing charges and time-of-use bands, plus itemised checking of a supplier statement against metered consumption and the rate you are contracted to pay, across every site, not just the total.',
            capability:
                'tariff-model / cost-calculation / bill-validation / bill-statement-model / bill-discrepancy-alert'
        },
        {
            title: 'Contract end dates across every site, in one place',
            detail: 'Record each site’s supplier, contract dates, notice period, volume tolerance and broker, with an alert as a renewal deadline approaches, a site lapses onto deemed rates, or usage is heading outside the volume your contract allows. At renewal, download a tender pack of each site’s consumption for suppliers to quote on.',
            capability:
                'supply-contract-registry / contract-renewal-alerts / contract-admin-ui / renewal-tender-pack'
        },
        {
            title: 'A login for each site manager',
            detail: 'Give a site manager read-only access to their own sites: the overview, alerts, closures and that site’s cost, plus their own weekly or monthly email, without seeing the rest of the estate.',
            capability:
                'location-scoped-access / report-subscriptions / alerts-notifications'
        },
        {
            title: 'Compared with a typical pub, restaurant or shop',
            detail: 'Each site’s kWh per m² set against published typical figures for its kind of building, so you know whether a site is high for a pub, not just high.',
            capability: 'static-benchmarks / org-profile-fields'
        },
        {
            title: 'See an overspend coming',
            detail: 'Budgets with a seasonally-projected year-end position and alerts on projected overrun, rather than a surprise at year end.',
            capability:
                'budget-model / budget-breach-alert / consumption-forecast'
        }
    ],

    asides: [
        {
            title: 'Closures and quiet weeks',
            body: 'Refurbishments, seasonal closures and quiet periods are recorded as Closures and costed the same way, so you can see what a site draws when it is shut, and whether that number improved after you acted on it. The product speaks your language throughout: your locations are Sites, and a shut day is closed, not a landlord’s "void".',
            capability: 'holiday-analysis-ui / holiday-analytics'
        },
        {
            title: 'Cold snaps are accounted for',
            body: 'Degree-day weather normalisation separates "we used more because it was freezing" from "we used more because something changed", so a winter comparison is worth having.',
            capability: 'degree-day-computation / weather-normalised-insights'
        }
    ],

    proofPoints: [
        'Half-hourly electricity and gas from your existing suppliers: no new hardware to install',
        'Every figure drills down to the interval data behind it',
        'Start with a handful of sites and add the rest by bulk import'
    ],

    ctaTitle: 'See one week of your own data',
    ctaBody:
        'The out-of-hours split is usually the fastest way to tell whether this is worth your time. We can show it on your sites, not a demo estate.'
}
